import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const DEFAULT_INCLUDES = [
  'Full PMS — scheduling, clinical, billing, reporting',
  'All RevTech products — InsightFlow, Denial IQ, ClaimVision, Payer IQ, Recon IQ',
  'AI workforce support — claim scrubbing, scheduling optimization, treatment sequencing',
  'NDS DataVault integration — enterprise analytics from day one',
  'Unlimited users per location',
  'Cloud hosting, backups, and updates',
  'Onboarding, training, and data migration',
  'Ongoing support and system administration',
];

const DEFAULT_FAQS = [
  { q: 'What PMS systems can you migrate from?', a: "We have proven migration paths from Dentrix, Dentrix Enterprise, Eaglesoft, Open Dental, Curve Dental, Cloud 9, and most other major dental PMS systems. Our team handles the data migration — you don't lift a finger." },
  { q: 'How long does implementation take?', a: 'A typical single-location implementation takes 2-4 weeks. Multi-location DSO rollouts are phased, usually 2-3 practices per week after the first location is validated.' },
  { q: 'Is there a long-term contract?', a: 'We offer both annual and month-to-month options. Annual contracts include a discount. There are no early termination fees — we earn your business every month.' },
  { q: 'Do we need NDS Managed IT services?', a: 'No. Xentrios is cloud-native and works on any internet connection. However, NDS Managed IT services complement Xentrios with endpoint management, HIPAA compliance, and cybersecurity for your practice infrastructure.' },
  { q: 'What about imaging integration?', a: 'Xentrios integrates with all major dental imaging systems including Dexis, Schick, Planmeca, and Apteryx. Images display directly within the patient chart.' },
  { q: 'Is my data secure?', a: 'Xentrios is HIPAA-compliant with encryption at rest and in transit, role-based access controls, audit logging, and regular security assessments. Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA.' },
];

export default function PricingPage() {
  const cmsPage = useCmsPage('xentrios', 'pricing');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Simple, transparent pricing.');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'One plan. Everything included. No hidden fees, no per-module charges, no surprises on your invoice.');
  const includes = c(cmsPage, 'pricing-tiers', 'items', DEFAULT_INCLUDES);
  const faqs = c(cmsPage, 'faq', 'items', DEFAULT_FAQS);
  return (
    <div>
      <Helmet><title>Pricing — Xentrios Dental PMS</title></Helmet>

      <section className="xen-hero" style={{ padding: '4rem 0 3rem' }}>
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Pricing</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
        </div>
      </section>

      <section className="xen-section xen-section-center">
        <div className="xen-container">
          <div className="xen-pricing-card">
            <p style={{ fontSize: '0.85rem', color: 'var(--xen-accent)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Per provider, per month</p>
            <p style={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.25rem' }}>Contact Us</p>
            <p style={{ color: '#888', marginBottom: '2rem' }}>Pricing based on practice size and configuration</p>

            <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
              <p style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Everything included:</p>
              {includes.map(item => (
                <p key={item} style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.5rem', paddingLeft: '1.25rem', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--xen-teal)' }}>✓</span> {item}
                </p>
              ))}
            </div>

            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ width: '100%', display: 'block', padding: '0.85rem', fontSize: '1rem', textAlign: 'center' }}>
              Get a Custom Quote
            </Link>
          </div>

          <div style={{ maxWidth: 600, margin: '3rem auto 0', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '1rem' }}>Volume pricing for DSOs</h3>
            <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.7 }}>
              Multi-location organizations receive volume-based pricing that decreases as you add practices.
              We also offer special pricing for organizations migrating from legacy PMS systems and for
              practices onboarded through NDS Practice Integration services.
            </p>
          </div>
        </div>
      </section>

      <section className="xen-section xen-section-light xen-section-center">
        <div className="xen-container">
          <h2>Frequently asked questions</h2>
          <div style={{ maxWidth: 650, margin: '2rem auto 0', textAlign: 'left' }}>
            {faqs.map(faq => (
              <div key={faq.q} style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e0e1eb' }}>
                <h4 style={{ marginBottom: '0.5rem' }}>{faq.q}</h4>
                <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.7 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
